/**
 * Fantasy Console 0 - Code Editor Tab
 *
 * Full-featured code editor with CodeMirror, multi-file support,
 * error display, and console log output.
 */

import { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import { useCart } from '../../context/CartContext'
import { useToast } from '../../context/ToastContext'
import { useConsole } from '../../context/ConsoleContext'
import {
  usePersistedTabState,
  usePersistedRef,
} from '../../context/EditorTabStateContext'
import {
  CodeEditor,
  ErrorPanel,
  CodeTemplates,
  EditorSettings,
  loadCodeEditorSettings,
  FileTabs,
  AddFileModal,
  VimModeIndicator,
  VimMode,
  CodeEditorRef,
  EditorPosition,
} from '../CodeEditor'
import { useConfirmDialog } from '../UI/ConfirmDialog'
import { CodeEditorSettings } from '../../utils/storage'
import { countTokensInFiles, MAX_TOKENS } from '../../utils/tokenCounter'
import styles from './CodeEditorTab.module.css'

const MAX_CODE_FILES = 8

export function CodeEditorTab() {
  const {
    cart,
    cartLoadVersion,
    updateCodeFile,
    addCodeFile,
    removeCodeFile,
    renameCodeFile,
  } = useCart()
  const { error: showError, success: showSuccess } = useToast()
  const { reload, clearErrors, errors } = useConsole()
  const editorRef = useRef<CodeEditorRef>(null)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showAddFile, setShowAddFile] = useState(false)
  const [editorSettings, setEditorSettings] =
    usePersistedTabState<CodeEditorSettings>(
      'code:editorSettings',
      loadCodeEditorSettings(),
    )
  const [activeFileIndex, setActiveFileIndex] = usePersistedTabState(
    'code:activeFileIndex',
    0,
  )
  const [vimMode, setVimMode] = usePersistedTabState<VimMode>(
    'code:vimMode',
    'NORMAL',
  )
  const { showConfirmDialog, confirmDialog } = useConfirmDialog()

  // Persisted editor cursor/scroll positions
  const editorPositions = usePersistedRef<Map<number, EditorPosition>>(
    'code:editorPositions',
    new Map(),
  )

  // Compute token counts for all files
  const tokenCounts = useMemo(
    () => countTokensInFiles(cart.codeFiles),
    [cart.codeFiles],
  )

  // Ensure activeFileIndex is valid
  useEffect(() => {
    if (activeFileIndex >= cart.codeFiles.length) {
      setActiveFileIndex(Math.max(0, cart.codeFiles.length - 1))
    }
  }, [activeFileIndex, cart.codeFiles.length])

  // Get active file
  const activeFile = cart.codeFiles[activeFileIndex] || cart.codeFiles[0]

  // Clear saved positions when a different cart is loaded (but not on remount)
  const prevCartLoadVersionRef = useRef(cartLoadVersion)
  useEffect(() => {
    if (prevCartLoadVersionRef.current !== cartLoadVersion) {
      editorPositions.current.clear()
      prevCartLoadVersionRef.current = cartLoadVersion
    }
  }, [cartLoadVersion])

  // Save position callback for CodeEditor unmount (tab/view switch)
  const handleSavePosition = useCallback(
    (position: EditorPosition) => {
      editorPositions.current.set(activeFileIndex, position)
    },
    [activeFileIndex],
  )

  const handleSelectFile = useCallback(
    (index: number) => {
      if (editorRef.current) {
        editorPositions.current.set(
          activeFileIndex,
          editorRef.current.getPosition(),
        )
      }
      setActiveFileIndex(index)
    },
    [activeFileIndex],
  )

  // Handle code changes from editor
  const handleCodeChange = useCallback(
    (newCode: string) => {
      updateCodeFile(activeFileIndex, newCode)
    },
    [updateCodeFile, activeFileIndex],
  )

  // Handle vim mode change
  const handleVimModeChange = useCallback((mode: VimMode) => {
    setVimMode(mode)
  }, [])

  // Handle run action
  const handleRun = useCallback(() => {
    clearErrors()
    reload()
  }, [clearErrors, reload])

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (code: string) => {
      updateCodeFile(activeFileIndex, code)
      clearErrors()
    },
    [updateCodeFile, activeFileIndex, clearErrors],
  )

  // Handle settings change
  const handleSettingsChange = useCallback(
    (newSettings: CodeEditorSettings) => {
      setEditorSettings(newSettings)
    },
    [],
  )

  // Jump to line in editor
  const handleJumpToLine = useCallback((line: number) => {
    console.log('Jump to line:', line)
  }, [])

  // Handle add file
  const handleAddFile = useCallback(
    (fileName: string) => {
      const success = addCodeFile(fileName)
      if (success) {
        setActiveFileIndex(cart.codeFiles.length)
        setShowAddFile(false)
        showSuccess(`Added file "${fileName}"`)
      } else {
        showError(
          'Failed to add file. Check if the name already exists or max files reached.',
        )
      }
    },
    [addCodeFile, cart.codeFiles.length, showSuccess, showError],
  )

  // Handle remove file
  const handleRemoveFile = useCallback(
    async (index: number) => {
      if (cart.codeFiles.length === 1) {
        showError('Cannot delete the last file')
        return
      }

      const fileName = cart.codeFiles[index]?.name
      const confirmed = await showConfirmDialog({
        title: 'Delete File',
        message: `Are you sure you want to delete "${fileName}"? This cannot be undone.`,
        confirmLabel: 'Delete',
        confirmVariant: 'danger',
      })
      if (!confirmed) return

      const success = removeCodeFile(index)
      if (success) {
        if (activeFileIndex >= cart.codeFiles.length - 1) {
          setActiveFileIndex(Math.max(0, cart.codeFiles.length - 2))
        }
        showSuccess(`Removed file "${fileName}"`)
      } else {
        showError('Failed to remove file')
      }
    },
    [
      removeCodeFile,
      cart.codeFiles,
      activeFileIndex,
      showSuccess,
      showError,
      showConfirmDialog,
    ],
  )

  // Handle rename file
  const handleRenameFile = useCallback(
    (index: number, newName: string) => {
      const success = renameCodeFile(index, newName)
      if (!success) {
        showError('Invalid file name or name already exists')
      }
    },
    [renameCodeFile, showError],
  )

  return (
    <div className={styles.container}>
      {/* Editor Pane */}
      <div className={styles.editorPane}>
        <div className={styles.editorHeader}>
          <div className={styles.editorHeaderLeft}>
            <button
              className={styles.toolbarButton}
              onClick={() => setShowTemplates(true)}
              title='Insert template code'
            >
              <TemplateIcon />
              <span>Templates</span>
            </button>
            <button
              className={styles.toolbarButton}
              onClick={() => setShowSettings(true)}
              title='Editor settings'
            >
              <SettingsIcon />
              <span className={styles.desktopOnly}>Settings</span>
            </button>
            {editorSettings.vimMode && <VimModeIndicator mode={vimMode} />}
          </div>
          <div className={styles.editorHeaderRight}>
            <span
              className={`${styles.tokenTotal} ${tokenCounts.total > MAX_TOKENS ? styles.tokenError : tokenCounts.total > 7000 ? styles.tokenWarning : styles.tokenOk}`}
            >
              {tokenCounts.total} / {MAX_TOKENS} tokens
            </span>
            <span className={styles.shortcutHint}>Ctrl+Enter to run</span>
            <button className='btn btn-primary btn-sm' onClick={handleRun}>
              <RunIcon />
              <span>Run</span>
            </button>
          </div>
        </div>

        {/* File Tabs */}
        <FileTabs
          files={cart.codeFiles}
          activeIndex={activeFileIndex}
          onSelectFile={handleSelectFile}
          onAddFile={() => setShowAddFile(true)}
          onRemoveFile={handleRemoveFile}
          onRenameFile={handleRenameFile}
          maxFiles={MAX_CODE_FILES}
          tokenCounts={tokenCounts.perFile}
        />

        <CodeEditor
          ref={editorRef}
          key={activeFileIndex}
          value={activeFile?.content || ''}
          onChange={handleCodeChange}
          onRun={handleRun}
          settings={editorSettings}
          onVimModeChange={
            editorSettings.vimMode ? handleVimModeChange : undefined
          }
          onSavePosition={handleSavePosition}
          initialPosition={editorPositions.current.get(activeFileIndex)}
        />

        {errors.length > 0 && (
          <ErrorPanel
            errors={errors}
            onClear={clearErrors}
            onJumpToLine={handleJumpToLine}
          />
        )}
      </div>

      {/* Templates Modal */}
      {showTemplates && (
        <CodeTemplates
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <EditorSettings
          onClose={() => setShowSettings(false)}
          onSettingsChange={handleSettingsChange}
        />
      )}

      {/* Add File Modal */}
      {showAddFile && (
        <AddFileModal
          existingNames={cart.codeFiles.map((f) => f.name)}
          onConfirm={handleAddFile}
          onCancel={() => setShowAddFile(false)}
        />
      )}

      {/* Delete File Confirm Dialog */}
      {confirmDialog}
    </div>
  )
}

function RunIcon() {
  return (
    <svg width='12' height='12' viewBox='0 0 12 12' fill='currentColor'>
      <path d='M2 1v10l9-5z' />
    </svg>
  )
}

function TemplateIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <rect x='2' y='2' width='10' height='10' rx='1' />
      <path d='M2 5h10M5 5v7' />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <circle cx='7' cy='7' r='2' />
      <path d='M7 1v2M7 11v2M1 7h2M11 7h2M2.5 2.5l1.5 1.5M10 10l1.5 1.5M2.5 11.5l1.5-1.5M10 4l1.5-1.5' />
    </svg>
  )
}
